"use client";

// Composant client pour une seule raison : le halo de la pastille « Live » est une animation INFINIE, donc elle doit
// se taire hors écran et en onglet caché. useLife() ne fait que ça. Tout ce que le lecteur doit lire — le titre, les
// quatre noms, les quatre catégories, les logos, les puces à venir — reste rendu par le serveur et se lit sans
// JavaScript : ce sont deux tableaux constants, pas une charge utile.

import { useEffect, useRef } from "react";
import Image from "next/image";
import SectionHeading from "@/components/ds/section-heading";
import Reveal from "@/components/motion/reveal";
import { stagger } from "@/components/motion/stagger";
import { brandText, type Brand } from "./brand";
import "./integrations.css";

/* Chaque marque garde SA boîte. Les quatre fichiers n'ont ni la même taille ni le même rapport
   (Clio 44 × 44, NetDocuments 40 × 29, SharePoint 1992 × 1946, Outlook 1831 × 1703) : les rendre
   tous en 36 × 36 laissait `contain` trancher tout seul, donc NetDocuments n'occupait que 36 × 26
   et ne pesait pas comme Clio, qui remplissait sa boîte. Le couple ci-dessous porte le RAPPORT de
   la marque — jamais une valeur d'affichage ; l'échelle optique, elle, se règle dans la feuille,
   une ligne par connecteur (--v2-logo-scale). Les .svg ne sont pas touchés : ce sont des marques
   déposées, on ne les recolore pas, on ne les recadre pas, on ne les déforme pas. */
const CONNECTORS = [
  { name: "NetDocuments", category: "Documents", logo: "netdocuments", box: [40, 29] },
  { name: "Clio", category: "Matters and time", logo: "clio-mark", box: [44, 44] },
  { name: "SharePoint", category: "Files", logo: "sharepoint", box: [1992, 1946] },
  { name: "Outlook", category: "Email and calendar", logo: "outlook", box: [1831, 1703] },
] as const;


/* Pose data-motion sur la grille : "live" (les quatre halos respirent), "idle" (ils sont mis en pause LÀ où ils en
   sont — hors écran, ou onglet caché), absent sous mouvement réduit (integrations.css n'anime que sous [data-motion],
   donc rien ne bouge du tout, et l'animation ne démarre jamais tant que personne n'est là pour la couper).
   Le patron est celui de components/home-v2/deploy.tsx, RECOPIÉ et non importé : chaque bloc garde son fichier. */
function useLife() {
  const ref = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const grid = ref.current;
    if (!grid) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;

    const update = () => {
      if (reduce.matches) delete grid.dataset.motion;
      else grid.dataset.motion = visible && !document.hidden ? "live" : "idle";
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = !!entry?.isIntersecting;
        update();
      },
      { threshold: 0 },
    );
    io.observe(grid);
    document.addEventListener("visibilitychange", update);
    reduce.addEventListener("change", update);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", update);
      reduce.removeEventListener("change", update);
    };
  }, []);

  return ref;
}

/* `brand` (brand.ts): "north" unless the /logo-test preview says "habit". */
export default function Integrations({ brand = "north" }: { brand?: Brand }) {
  const grid = useLife();

  return (
    <Reveal as="section" id="integrations" className="v2-integrations" aria-label="Integrations">
      <div className="v2-integrations__inner">
        <SectionHeading
          tag="Integrations"
          tagVariant="blue"
          title={["Plugged into the tools", "your firm already runs on."]}
          lead={brandText("North OS connects Claude to where the work already lives. Nothing to migrate, nothing to re-learn.", brand)}
          reveal
        />
        <ul ref={grid} className="v2-integrations__grid">
          {CONNECTORS.map((connector, i) => (
            <li
              key={connector.name}
              className="v2-integrations__card v2-reveal"
              data-connector={connector.logo}
              style={stagger(3 + i)}
            >
              <div className="v2-integrations__top">
                <div className="v2-integrations__logo">
                  <Image
                    src={`/assets/integrations/${connector.logo}.svg`}
                    alt=""
                    width={connector.box[0]}
                    height={connector.box[1]}
                  />
                </div>
                <span className="v2-integrations__status"><span aria-hidden="true" />Live</span>
              </div>
              <h3 className="v2-integrations__name">{connector.name}</h3>
              <p className="v2-integrations__category">{connector.category}</p>
            </li>
          ))}
        </ul>
        <div className="v2-integrations__upcoming v2-reveal" style={stagger(7)}>
          <p className="v2-integrations__caption">And many more</p>
        </div>
      </div>
    </Reveal>
  );
}
